// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

import type { SchemaMigration } from '../types'

/**
 * Add `description` field to missions schema.
 *
 * ProposalService carries the proposal description forward to the mission,
 * but the v1 schema omitted the field. This migration adds it as an optional
 * richtext field and bumps all existing records to v2 (no data transform
 * needed — existing missions simply have no description).
 */
export const migration: SchemaMigration = {
  schema: 'missions',
  version: 2,
  description: 'Add description field to missions schema',
  schemaChanges: schema => {
    const existingFields: Record<string, unknown>[] = schema.fields ?? []

    // Insert description after title (order 2), bump subsequent orders
    const updatedFields: Record<string, unknown>[] = existingFields.map(f => {
      const order = (f.order as number) ?? 0
      return {
        ...f,
        order: order >= 2 ? order + 1 : order,
      }
    })

    updatedFields.push({
      name: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      order: 2,
      config: { placeholder: 'Mission description carried from proposal' },
    })

    return {
      ...schema,
      version: 2,
      fields: updatedFields,
    }
  },
}
